<template>
  <div class="home-cost-calendar homeCalendarTechShell">
    <div class="homeCalendarTech">
      <div class="cal-header">
        <div class="cal-title">{{ monthTitle }}</div>
        <el-button-group>
          <el-button type="plain" size="mini" @click="prevMonth">上个月</el-button>
          <el-button type="plain" size="mini" @click="goToday">今天</el-button>
          <el-button type="plain" size="mini" @click="nextMonth">下个月</el-button>
        </el-button-group>
      </div>
      <div class="cal-body">
        <div class="cal-weekdays">
          <span v-for="(w, i) in weekLabels" :key="i" class="cal-weekday">{{ w }}</span>
        </div>
        <div class="cal-dates">
          <div
            v-for="cell in cells"
            :key="cell.dateStr"
            class="cal-cell-wrap"
            :class="[wrapClass(cell), { 'cell-selected': isSelected(cell) }]"
          >
            <div
              class="cal-cell-inner"
              :class="{
                'is-muted': cell.type !== 'current',
                'is-selected': isSelected(cell),
                'is-today': isToday(cell) && !isSelected(cell),
              }"
              @click="selectDay(cell)"
              @dblclick.stop="onDayDblclick(cell)"
            >
              <template v-if="cell.type === 'current'">
                <span class="costDay">{{ cell.dayNum }}</span>
                <span
                  v-if="incomeMap[cell.dateStr]"
                  class="income-amount"
                >{{ formatIncomeInteger(incomeMap[cell.dateStr]) }}</span>
                <div v-if="costMap[cell.dateStr]" class="moneyShowClass">
                  <span class="cost-amount">{{ costMap[cell.dateStr] || "0" }}</span>
                </div>
              </template>
              <template v-else>
                <span class="costDay costDay-muted">{{ cell.dayNum }}</span>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
function pad(n) {
  return String(n).padStart(2, "0");
}

function toYMD(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export default {
  name: "HomeCostCalendar",
  props: {
    value: {
      type: Date,
      required: true,
    },
    costMap: {
      type: Object,
      default: () => ({}),
    },
    incomeMap: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    valueDate() {
      const v = this.value;
      return v instanceof Date && !isNaN(v.getTime()) ? v : new Date();
    },
    monthTitle() {
      const d = this.valueDate;
      return `${d.getFullYear()} 年 ${d.getMonth() + 1} 月`;
    },
    selectedYMD() {
      return toYMD(this.valueDate);
    },
    todayYMD() {
      return toYMD(new Date());
    },
    weekLabels() {
      return ["一", "二", "三", "四", "五", "六", "日"];
    },
    cells() {
      const v = this.valueDate;
      const y = v.getFullYear();
      const m = v.getMonth();
      const first = new Date(y, m, 1);
      const startDay = (first.getDay() + 6) % 7;
      const daysInMonth = new Date(y, m + 1, 0).getDate();
      const prevMonthLast = new Date(y, m, 0).getDate();

      const list = [];
      for (let i = 0; i < startDay; i++) {
        const day = prevMonthLast - startDay + i + 1;
        const d = new Date(y, m - 1, day);
        list.push({ type: "prev", dateStr: toYMD(d), dayNum: day });
      }
      for (let d = 1; d <= daysInMonth; d++) {
        const date = new Date(y, m, d);
        list.push({ type: "current", dateStr: toYMD(date), dayNum: d });
      }
      let next = 1;
      while (list.length < 42) {
        const d = new Date(y, m + 1, next);
        list.push({ type: "next", dateStr: toYMD(d), dayNum: next });
        next++;
      }
      return list;
    },
  },
  methods: {
    isSelected(cell) {
      return cell.dateStr === this.selectedYMD;
    },
    isToday(cell) {
      return cell.dateStr === this.todayYMD;
    },
    wrapClass(cell) {
      return {
        "cell-prev": cell.type === "prev",
        "cell-next": cell.type === "next",
        "cell-current": cell.type === "current",
      };
    },
    selectDay(cell) {
      const parts = cell.dateStr.split("-").map(Number);
      this.$emit("input", new Date(parts[0], parts[1] - 1, parts[2]));
    },
    onDayDblclick(cell) {
      if (cell.type === "current") {
        this.$emit("day-dblclick", cell.dateStr);
      }
    },
    prevMonth() {
      const v = this.valueDate;
      this.$emit("input", new Date(v.getFullYear(), v.getMonth() - 1, 1));
    },
    nextMonth() {
      const v = this.valueDate;
      this.$emit("input", new Date(v.getFullYear(), v.getMonth() + 1, 1));
    },
    goToday() {
      const t = new Date();
      this.$emit("input", new Date(t.getFullYear(), t.getMonth(), t.getDate()));
    },
    /** 日历展示：收入保留整数（去掉小数） */
    formatIncomeInteger(raw) {
      if (raw == null || raw === "") {
        return "";
      }
      const s = String(raw).trim();
      const num = parseFloat(s.replace(/^\+/, ""));
      if (isNaN(num)) {
        return raw;
      }
      const n = Math.round(Math.abs(num));
      return s.startsWith("-") ? `-${n}` : `+${n}`;
    },
  },
};
</script>

<style lang="css" scoped>
.home-cost-calendar.homeCalendarTechShell {
  position: relative;
  box-sizing: border-box;
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  max-width: 100%;
  height: 100%;
  padding: 10px 12px;
  border-radius: 14px;
  background:
    linear-gradient(155deg, rgba(12, 18, 38, 0.98) 0%, rgba(8, 12, 28, 0.99) 42%, rgba(17, 24, 39, 0.94) 100%),
    radial-gradient(ellipse 120% 80% at 50% -20%, rgba(56, 189, 248, 0.12), transparent 55%),
    radial-gradient(ellipse 90% 60% at 100% 100%, rgba(129, 140, 248, 0.08), transparent 50%);
  border: 1px solid rgba(56, 189, 248, 0.28);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.05) inset,
    0 10px 28px rgba(2, 6, 23, 0.75),
    0 0 0 1px rgba(2, 6, 23, 0.85),
    0 0 20px rgba(56, 189, 248, 0.14);
}

.home-cost-calendar.homeCalendarTechShell::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  background: linear-gradient(
    125deg,
    rgba(56, 189, 248, 0.09) 0%,
    transparent 38%,
    transparent 62%,
    rgba(129, 140, 248, 0.06) 100%
  );
  opacity: 0.95;
}

.home-cost-calendar.homeCalendarTechShell::after {
  content: "";
  position: absolute;
  inset: 1px;
  border-radius: 13px;
  pointer-events: none;
  border: 1px solid rgba(255, 255, 255, 0.04);
  box-shadow: inset 0 0 24px rgba(56, 189, 248, 0.03);
}

.homeCalendarTech {
  position: relative;
  z-index: 1;
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  max-width: 100%;
  height: 100%;
  border-radius: 10px;
  overflow: hidden;
}

.cal-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  box-sizing: border-box;
  padding: 8px 12px 10px;
  margin: 0 0 2px;
  border-bottom: 1px solid rgba(56, 189, 248, 0.2);
  background: linear-gradient(
    180deg,
    rgba(15, 23, 42, 0.55) 0%,
    rgba(15, 23, 42, 0.2) 100%
  );
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.04) inset;
  border-radius: 8px 8px 0 0;
}

.cal-title {
  color: #e0f2fe;
  font-weight: 700;
  font-size: 14px;
  letter-spacing: 0.05em;
  text-shadow:
    0 0 12px rgba(56, 189, 248, 0.45),
    0 0 24px rgba(129, 140, 248, 0.25);
}

.cal-body {
  position: relative;
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  box-sizing: border-box;
  padding: 6px 8px 8px;
  background: rgba(2, 6, 23, 0.3);
  border-radius: 0 0 8px 8px;
  box-shadow: inset 0 1px 8px rgba(0, 0, 0, 0.35);
}

.cal-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  flex-shrink: 0;
  gap: 0;
  padding-bottom: 4px;
  border-bottom: 1px solid rgba(56, 189, 248, 0.16);
}

.cal-weekday {
  padding: 4px 0 6px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-align: center;
  color: rgba(148, 163, 184, 0.95);
  text-shadow: 0 0 8px rgba(56, 189, 248, 0.2);
}

.cal-dates {
  flex: 1 1 auto;
  min-height: 0;
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-template-rows: repeat(6, minmax(0, 1fr));
  gap: 1px;
  padding: 0;
  background: rgba(56, 189, 248, 0.12);
}

.cal-cell-wrap {
  min-height: 0;
  box-sizing: border-box;
  transition: background 0.2s ease, box-shadow 0.2s ease;
}

.cell-prev,
.cell-next {
  background: rgba(15, 23, 42, 0.35);
}

.cell-current {
  background: rgba(2, 6, 23, 0.4);
}

.cal-cell-inner {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  line-height: 1.15;
  box-sizing: border-box;
  margin: 1px;
  padding: 3px 4px 4px;
  border-radius: 6px;
  background: linear-gradient(
    165deg,
    rgba(15, 23, 42, 0.92) 0%,
    rgba(15, 23, 42, 0.72) 45%,
    rgba(8, 12, 28, 0.88) 100%
  );
  border: 1px solid rgba(56, 189, 248, 0.14);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.03) inset,
    0 -1px 0 rgba(0, 0, 0, 0.35) inset,
    0 2px 6px rgba(0, 0, 0, 0.28);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;
}

.cal-cell-inner.is-muted {
  opacity: 0.45;
}

.cal-cell-inner.is-today {
  box-shadow: inset 0 0 0 1px rgba(56, 189, 248, 0.45);
}

.cal-cell-inner.is-selected {
  background: linear-gradient(
    155deg,
    rgba(30, 58, 138, 0.5) 0%,
    rgba(15, 23, 42, 0.88) 55%,
    rgba(12, 18, 38, 0.95) 100%
  );
  border-color: rgba(56, 189, 248, 0.5);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.06) inset,
    0 0 0 1px rgba(56, 189, 248, 0.22),
    0 0 16px rgba(56, 189, 248, 0.22);
}

.cal-cell-wrap.cell-selected {
  background: rgba(56, 189, 248, 0.08);
  box-shadow: inset 0 0 24px rgba(56, 189, 248, 0.12);
}

.cal-cell-inner:hover {
  border-color: rgba(103, 232, 249, 0.35);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.05) inset,
    0 0 12px rgba(56, 189, 248, 0.18);
}

.cal-cell-inner.is-selected:hover {
  border-color: rgba(103, 232, 249, 0.55);
}

.costDay {
  flex-shrink: 0;
  align-self: flex-start;
  max-width: 42%;
  color: rgba(186, 230, 253, 0.98);
  font-weight: bold;
  font-size: 12px;
  line-height: 1.2;
}

.costDay-muted {
  color: rgba(148, 163, 184, 0.9);
}

.cal-cell-inner.is-selected .costDay {
  text-shadow:
    0 0 10px rgba(56, 189, 248, 0.65),
    0 0 20px rgba(129, 140, 248, 0.35);
}

.cost-amount {
  font-size: 9px;
  color: #fca5a5;
  text-shadow: 0 0 8px rgba(248, 113, 113, 0.45);
}

.income-amount {
  position: absolute;
  top: 2px;
  right: 3px;
  left: auto;
  max-width: 56%;
  text-align: right;
  font-size: 9px;
  font-weight: 600;
  line-height: 1.15;
  color: #6ee7b7;
  word-break: break-all;
  text-shadow:
    0 0 6px rgba(16, 185, 129, 0.55),
    0 0 10px rgba(52, 211, 153, 0.45);
  z-index: 1;
}

.moneyShowClass {
  margin-top: auto;
  padding-top: 2px;
  line-height: 1.15;
  width: 100%;
}
</style>
